/*
 * Internal helper: Do NOT import outside of logger.ts.
 * Purpose: Forward sanitized warn/error events to Rollbar if available in the browser global.
 */

import { DEBUG_LEVEL } from '../../../config/configConstants';
import { isObject, isPlainObject, isString } from 'lodash';

import { splitLogValues, type ExternalLogDestination } from './loggerExternalDestinations';

export type RollbarLike = {
    error?: (message: string, error?: unknown, payload?: unknown) => void;
    warning?: (message: string, error?: unknown, payload?: unknown) => void;
};

type RollbarMethodName = keyof Pick<RollbarLike, 'error' | 'warning'>;
type RollbarPayload = {
    fingerprint?: string;
    custom?: unknown;
};
type RollbarPayloadOverride = RollbarPayload & Record<string, unknown>;

const isRecord = (value: unknown): value is Record<string, unknown> => isObject(value);

/**
 * Detects metadata objects that should be forwarded as Rollbar payload fields instead of generic extra data.
 */
const isRollbarPayloadOverride = (value: unknown): value is RollbarPayloadOverride =>
    isRecord(value) && (isString(value.fingerprint) || 'custom' in value);

const hasObjectKeys = (value: Record<string, unknown>): boolean => Object.keys(value).length > 0;

const getPayloadMetadata = (value: RollbarPayloadOverride): Record<string, unknown> =>
    Object.entries(value).reduce<Record<string, unknown>>((metadata, [key, metadataValue]) => {
        if (key === 'fingerprint' || key === 'custom') return metadata;

        metadata[key] = metadataValue;
        return metadata;
    }, {});

const mergeCustomPayload = (payload: RollbarPayload, custom: unknown) => {
    if (!('custom' in payload)) {
        payload.custom = custom;
        return;
    }

    if (isPlainObject(payload.custom) && isPlainObject(custom)) {
        payload.custom = {
            ...(payload.custom as Record<string, unknown>),
            ...(custom as Record<string, unknown>),
        };
        return;
    }

    payload.custom = custom;
};

const getPayloadCustomOverride = (value: RollbarPayloadOverride): unknown => {
    const metadata = getPayloadMetadata(value);
    const { custom } = value;
    if (!hasObjectKeys(metadata)) return custom;

    if ('custom' in value && isPlainObject(custom)) {
        return {
            ...metadata,
            ...(custom as Record<string, unknown>),
        };
    }

    if ('custom' in value) {
        return {
            ...metadata,
            custom,
        };
    }

    return metadata;
};

/**
 * Applies supported Rollbar payload fields, allowing later metadata arguments to override earlier ones.
 */
const mergeRollbarPayloadOverride = (payload: RollbarPayload, value: RollbarPayloadOverride) => {
    if (isString(value.fingerprint)) {
        payload.fingerprint = value.fingerprint;
    }

    const custom = getPayloadCustomOverride(value);
    if (custom !== undefined) {
        mergeCustomPayload(payload, custom);
    }
};

/**
 * Builds the custom payload sent to Rollbar, preserving default extras unless a caller supplies custom data.
 */
const buildRollbarPayload = (extra: unknown[], payloadOverrides: RollbarPayload): RollbarPayload | undefined => {
    const hasPayloadOverride = 'fingerprint' in payloadOverrides || 'custom' in payloadOverrides;
    if (!extra.length) return hasPayloadOverride ? payloadOverrides : undefined;

    if (isPlainObject(payloadOverrides.custom)) {
        return {
            ...payloadOverrides,
            custom: {
                extra,
                ...(payloadOverrides.custom as Record<string, unknown>),
            },
        };
    }

    return {
        custom: {
            extra,
        },
        ...payloadOverrides,
    };
};

/**
 * Invoke a Rollbar logging method (error or warning) with the right argument shape.
 * Mirrors Rollbar signature precedence:
 *  - message, error, custom
 *  - message, error
 *  - message, custom
 *  - message
 */
const invokeRollbar = (
    rollbar: RollbarLike,
    methodName: RollbarMethodName,
    message: string,
    error: unknown,
    payload: RollbarPayload | undefined,
) => {
    const method = rollbar[methodName];
    if (!method) return;

    // Preserve Rollbar's argument precedence exactly.
    if (error) {
        if (payload) {
            method.call(rollbar, message, error, payload);
        } else {
            method.call(rollbar, message, error);
        }
        return;
    }

    if (payload) {
        method.call(rollbar, message, payload);
        return;
    }

    method.call(rollbar, message);
};

/** Resolve a Rollbar-like instance from the global scope (window in browsers; Capacitor / hybrid contexts vary). */
const resolveRollbar = (): RollbarLike | undefined => (globalThis as { Rollbar?: RollbarLike }).Rollbar;

/**
 * For internal logger usage only. Relies on prior sanitization performed in logger.ts.
 */
export const forwardWarnOrErrorToRollbar = (logLevel: number, values: unknown[]) => {
    /**
     * Expectations (done by logger.ts before calling us):
     *  - values[0] is ideally a string message (not guaranteed)
     *  - remaining values may include: an Error (at most one is forwarded) + arbitrary metadata
     *  - all values are already considered safe / sanitized for client logging
     */
    try {
        // Early exit: only WARN / ERROR levels are forwarded. Avoid touching globals otherwise.
        const isErrorLevel = logLevel === DEBUG_LEVEL.ERROR;
        const isWarnLevel = logLevel === DEBUG_LEVEL.WARN;
        if (!isErrorLevel && !isWarnLevel) return;

        const rollbar = resolveRollbar();
        if (!rollbar) return; // No Rollbar present; silently ignore.

        // Message (with a placeholder if not a string), first Error, and the remaining values for context.
        const { message, firstError, rest } = splitLogValues(values);

        const extra: unknown[] = [];
        const payloadOverrides: RollbarPayload = {};
        for (const value of rest) {
            if (isRollbarPayloadOverride(value)) {
                mergeRollbarPayloadOverride(payloadOverrides, value);
                continue;
            }
            if (value !== undefined) extra.push(value);
        }

        // Rollbar supports sending arbitrary data via payload.custom. Explicit custom/fingerprint fields are forwarded
        // at the payload root so callers can control grouping and custom payload structure.
        const payload = buildRollbarPayload(extra, payloadOverrides);

        // Dispatch to the appropriate Rollbar method. (We intentionally do not fallback: warn != error.)
        if (isErrorLevel && rollbar.error) {
            invokeRollbar(rollbar, 'error', message, firstError, payload);
            return;
        }
        if (isWarnLevel && rollbar.warning) {
            invokeRollbar(rollbar, 'warning', message, firstError, payload);
            return;
        }
        // If the specific Rollbar method is missing, we silently drop.
    } catch (error: unknown) {
        // Last-resort: never let forwarding break app flow. Log a minimal console error for internal debugging.
        const errorMessage =
            error && typeof error === 'object' && 'message' in error
                ? (error as { message?: string }).message
                : undefined;
        console.error('[rollbar-forward-failed]', errorMessage);
    }
};

export const rollbarLogDestination: ExternalLogDestination = {
    isLoggingEnabled: () => !!resolveRollbar(),
    forward: forwardWarnOrErrorToRollbar,
};
